import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '@/store/store'
import { isRelatedFederalContent } from '@/jurisdiction-mappings/jurisdiction-maps'
import { CIRCUIT_BASED_FEDERAL_COURTS, TOP_LEVEL_FEDERAL_COURTS } from '../jurisdiction-sources/federal-jurisdictions-mappings'

/**
 * Select the entire research filters state for a conversation
 */
export const selectResearchFiltersForConversation = createSelector(
  (state: RootState, props: { conversationId: string }) => state.researchFiltersState.filters[props.conversationId],
  (filters) => filters ?? null
)

/**
 * Select federal sources (LRR + caselaw)
 */
export const selectFederalSources = createSelector(selectResearchFiltersForConversation, (filters) => {
  if (!filters) return { lrr: [], caselaw: [] }
  return {
    lrr: filters.federalLrrSources,
    caselaw: filters.federalCaselawSources,
  }
})

/**
 * Select the selected state names (array, may be empty)
 */
export const selectSelectedStateNames = createSelector(selectResearchFiltersForConversation, (filters) => {
  return filters?.selectedStateNames ?? []
})

/**
 * Select source-code groups shown as selectable rows in the Federal tab.
 * Uses the same courts dataset that powers the Federal tab UI.
 */
const FEDERAL_TAB_SOURCE_CODE_GROUPS = [
  ...TOP_LEVEL_FEDERAL_COURTS.map((court) => court.value.federalCourtJurisdictionCodes),
  ...CIRCUIT_BASED_FEDERAL_COURTS.flatMap((circuit) => circuit.children.map((child) => child.codes)),
]

const selectFederalTabSourceCodeGroups = (_state: RootState) => FEDERAL_TAB_SOURCE_CODE_GROUPS

/**
 * Select total number of selectable source rows available in the Federal tab.
 */
export const selectTotalFederalTabSourceCount = createSelector(selectFederalTabSourceCodeGroups, (codeGroups) => codeGroups.length)

/**
 * Select the number of current sources shown in federal tab.
 */
export const selectNumberOfCurrentSourcesShownInFederalTab = createSelector(
  selectFederalSources,
  selectFederalTabSourceCodeGroups,
  (federalSources, federalTabCodeGroups) => {
    const selectedFederalCodes = new Set<string>([...federalSources.caselaw, ...federalSources.lrr])
    return federalTabCodeGroups.filter((codes) => codes.every((code) => selectedFederalCodes.has(code))).length
  }
)

/**
 * Checks if all sources shown in the Federal tab are currently selected.
 */
export const selectAreAllFederalTabSourcesSelected = createSelector(
  selectNumberOfCurrentSourcesShownInFederalTab,
  selectTotalFederalTabSourceCount,
  (selectedCount, totalCount) => {
    return totalCount > 0 && selectedCount === totalCount
  }
)

/**
 * Check if "Include related federal content" is enabled.
 * Derived from whether any current federal sources match the related-content mapping for selected states.
 * Jurisdiction codes are unique across LRR and caselaw, so a single pass checking both properties is safe.
 */
export const selectHasRelatedFederalContent = createSelector(selectResearchFiltersForConversation, (filters) => {
  if (!filters || filters.selectedStateNames.length === 0) return false
  return [...filters.federalLrrSources, ...filters.federalCaselawSources].some((code) =>
    filters.selectedStateNames.some((name) => {
      const { isRelatedLrr, isRelatedCaselaw } = isRelatedFederalContent(code, name)
      return isRelatedLrr || isRelatedCaselaw
    })
  )
})

/**
 * Select display description for research sources based on selection in the State tab
 * @returns The description string for display in the State tab (e.g. "California", "California, Texas")
 */
export const selectResearchFiltersDisplayForStates = createSelector(selectResearchFiltersForConversation, (filters) => {
  if (!filters || filters.selectedStateNames.length === 0) return ''

  return filters.selectedStateNames.map((name) => name.replace(/\b\w/g, (l) => l.toUpperCase())).join(', ')
})

/**
 * Select display description for research sources selected shown in the Source selection dialog
 *
 * Rules:
 * - If only federal sources are selected: "Federal (number of sources)"
 * - If one or more states are selected: Comma-separated state names (e.g. "California" or "California, Texas")
 * - If states + federal are selected: "State Names + Federal (number of sources)"
 *
 * @returns The description string for display in UI
 */
export const selectResearchFiltersDisplay = createSelector(
  selectResearchFiltersDisplayForStates,
  selectNumberOfCurrentSourcesShownInFederalTab,
  (displayDescriptionForStates, numberOfCurrentSourcesShownInFederalTab): string => {
    // If state is selected, return the state name + federal (number of sources)
    if (displayDescriptionForStates) {
      const federalSuffix = numberOfCurrentSourcesShownInFederalTab > 0 ? ` + Federal (${numberOfCurrentSourcesShownInFederalTab})` : ''
      return displayDescriptionForStates + federalSuffix
    }
    // If only federal sources are selected, return "Federal (number of sources"
    return `Federal (${numberOfCurrentSourcesShownInFederalTab})`
  }
)
